import icons from "@/setup/icons.js";
import { lazy, Suspense } from "react";
import AuthProvider from "../api/auth/AuthProvider.jsx";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import {
  ACCOUNT_SETTINGS_ROUTE,
  HOME_ROUTE,
  LOGIN_ROUTE,
  SIGN_UP_ROUTE,
  USER_INFO,
  RESET_PASSWORD_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  VERIFY_EMAIL_ROUTE,
  REGISTRATION_COMPLETE_ROUTE,
  ABSTRACT_SUMMARY_ROUTE,
  ACCESS_DENIED_ROUTE,
  BASE_ROUTE,
  ACCOUNT_DELETED_ROUTE,
  JOB_DASHBOARD_ROUTE,
} from "../routing/app-routes.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ProtectedRoute from "./routing/ProtectedRoute.jsx";
import { ThemeProvider } from "./providers/theme/ThemeContext.jsx";
import Toast from "@/components/common/toasts/toast.jsx";
import NotFoundPage from "./pages/not-found/NotFoundPage.jsx";
import ErrorBoundary from "./routing/error-boundary/ErrorBoundary.jsx";
import AccessDenied from "./pages/access-denied/AccessDeined.jsx";
import RedirectToHome from "./routing/RedirectToHome.jsx";
import { useGlobalToastStore } from "@/state/toast-store.js";
import MainSpinner from "./common/loaders/main-spinner/MainSpinner.jsx";

icons();
const HomePage = lazy(
  () => import("./pages/document-list-page/DocumentListPage.jsx")
);
const LoginPage = lazy(
  () => import("./pages/login-page/components/LoginPage.jsx")
);
const SignUpPage = lazy(
  () => import("./pages/sign-up-page/components/sign-up-page.jsx")
);
const UserInfoPage = lazy(
  () => import("./pages/user-info-page/UserInfoPage.jsx")
);
const ResetPassword = lazy(
  () => import("./pages/reset-password/ResetPassword.jsx")
);
const ForgotPassword = lazy(
  () => import("./pages/forgot-password/ForgotPassword.jsx")
);
const Navbar = lazy(
  () => import("./navigation/global-nav-bar/GlobalNavBar.jsx")
);
const AccountSettings = lazy(
  () => import("./pages/account-settings-page/AccountSettingsPage.jsx")
);
const RegistrationComplete = lazy(
  () => import("./pages/email-verification/RegistrationComplete.jsx")
);
const EmailVerification = lazy(
  () => import("./pages/email-verification/EmailVerification.jsx")
);
const AbstractSummaryPage = lazy(
  () => import("./pages/abstract-summary-page/AbstractSummaryPage.jsx")
);

const AccountDeletedPage = lazy(
  () => import("./pages/account-deleted-page/AccountDeleted.jsx")
);

const Dashboard = lazy(
  () =>
    import("../job-dashboard/components/pages/job-dashboard/JobDashboard.jsx")
);

const withErrorBoundary = (element) => <ErrorBoundary>{element}</ErrorBoundary>;

const router = createBrowserRouter([
  {
    path: LOGIN_ROUTE,
    element: withErrorBoundary(<LoginPage />),
  },
  {
    path: SIGN_UP_ROUTE,
    element: withErrorBoundary(<SignUpPage />),
  },
  {
    path: FORGOT_PASSWORD_ROUTE,
    element: withErrorBoundary(<ForgotPassword />),
  },
  {
    path: RESET_PASSWORD_ROUTE,
    element: withErrorBoundary(<ResetPassword />),
  },
  {
    path: VERIFY_EMAIL_ROUTE,
    element: withErrorBoundary(<EmailVerification />),
  },
  {
    path: REGISTRATION_COMPLETE_ROUTE,
    element: withErrorBoundary(
      <ProtectedRoute>
        <RegistrationComplete />
      </ProtectedRoute>
    ),
  },
  {
    path: USER_INFO,
    element: withErrorBoundary(
      <ProtectedRoute>
        <UserInfoPage />
      </ProtectedRoute>
    ),
  },
  {
    //Routes with a workspaceId in the path
    path: HOME_ROUTE,
    element: withErrorBoundary(
      <ProtectedRoute>
        <Navbar />
      </ProtectedRoute>
    ),
    children: [
      {
        path: HOME_ROUTE,
        element: <HomePage />,
      },
      {
        path: ABSTRACT_SUMMARY_ROUTE,
        element: <AbstractSummaryPage />,
      },
      {
        path: ACCOUNT_SETTINGS_ROUTE,
        element: <AccountSettings />,
      },
    ],
  },
  {
    path: BASE_ROUTE,
    element: (
      <ProtectedRoute>
        <RedirectToHome />
      </ProtectedRoute>
    ),
  },
  {
    path: ACCOUNT_DELETED_ROUTE,
    element: <AccountDeletedPage />,
  },
  {
    path: ACCESS_DENIED_ROUTE,
    element: withErrorBoundary(<AccessDenied />),
  },
  {
    path: "*",
    element: withErrorBoundary(<NotFoundPage />),
  },
  {
    path: JOB_DASHBOARD_ROUTE,
    element: withErrorBoundary(
      <ProtectedRoute>
        <Suspense fallback={<MainSpinner />}>
          <Navbar></Navbar>
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        path: JOB_DASHBOARD_ROUTE,
        element: <Dashboard />,
      },
    ],
  },
]);

// Create a client
const queryClient = new QueryClient();
function App() {
  return (
    <ThemeProvider>
      <div id="App" className="App absolute-min">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </div>
      <Toast storeRef={useGlobalToastStore} />
    </ThemeProvider>
  );
}

export default App;
